import Blob from "cross-blob";
import {
  Document,
  HeadingLevel,
  ISectionOptions,
  Packer,
  Paragraph,
  TextRun,
} from "docx";
import flattenDeep from "lodash/flattenDeep";

import { ApplicationFull } from "@/lib/prisma";

import { docStyles, renderDateRange } from "./common";
import { numberingStyle } from "./common/numbering";
import { DocGenerator } from "../";
import { renderProseMirrorSchemaToGoogleDoc } from "../proseMirror";
import { ApplicantEntrepreneurshipFull } from "../../prisma";
import { parseProseMirrorSchema } from "../../proseMirror";

interface EntrepreneurshipDocArgs {
  applicantEntrepreneurship: ApplicantEntrepreneurshipFull;
  application: ApplicationFull;
}

export const createEntrepreneurshipDoc: DocGenerator<
  EntrepreneurshipDocArgs
> = async (t, { application, applicantEntrepreneurship }) => {
  const document = new Document({
    styles: docStyles,
    numbering: numberingStyle,
    sections: [
      {
        children: flattenDeep<ISectionOptions["children"][0] | null>([
          new Paragraph({
            children: [
              new TextRun({
                text: "Applicant: ",
              }),
              new TextRun({
                text: application.applicant.fullName
                  ? application.applicant.fullName
                  : `${application.applicant.firstName} ${application.applicant.lastName}`,
              }),
            ],
            heading: HeadingLevel.TITLE,
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Entrepreneurship at ",
              }),
              new TextRun({
                text: parseProseMirrorSchema(applicantEntrepreneurship.name),
              }),
            ],
            heading: HeadingLevel.TITLE,
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Worked at ",
              }),
              ...renderDateRange(
                applicantEntrepreneurship.dateFrom,
                applicantEntrepreneurship.dateTo,
                applicantEntrepreneurship.workingNow
              ),
            ],
          }),

          new Paragraph({
            style: "wellSpaced",
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Executive summary about the company: ",
                bold: true,
              }),
            ],
          }),

          renderProseMirrorSchemaToGoogleDoc(
            applicantEntrepreneurship.briefDescription,
            "-"
          ),

          new Paragraph({
            children: [
              new TextRun({
                text: "Why was your solution better than other competitors' on the market?: ",
                bold: true,
              }),
            ],
          }),

          renderProseMirrorSchemaToGoogleDoc(
            applicantEntrepreneurship.novelty,
            "-"
          ),

          new Paragraph({
            children: [
              new TextRun({
                text: "What was your direct responsibility in the company/ its core product?: ",
                bold: true,
              }),
            ],
          }),

          renderProseMirrorSchemaToGoogleDoc(
            applicantEntrepreneurship.responsibility,
            "-"
          ),

          new Paragraph({
            children: [
              new TextRun({
                text: "List any metrics showing the product/company was successfull: ",
                bold: true,
              }),
            ],
          }),

          renderProseMirrorSchemaToGoogleDoc(
            applicantEntrepreneurship.metrics,
            "-"
          ),

          new Paragraph({
            children: [
              new TextRun({
                text: `Link to Pitch Deck: `,
                bold: true,
              }),
            ],
          }),

          renderProseMirrorSchemaToGoogleDoc(
            applicantEntrepreneurship.pitchDeckUrl,
            "-"
          ),

          ...applicantEntrepreneurship.applicantMilestoneList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Milestone: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: parseProseMirrorSchema(x.name),
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: 'What was the milestone? "Achievement": ',
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.milestoneAim, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "How did you contribute to it?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.whatDidYouDo, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "What was challenging about achieving this milestone?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.challenging, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "How did achieving this milestone impact the company or its product?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.milestoneImpact, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any numbers or metrics to demonstrate the impact of this achievement on your company?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.metrics, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Was this milestone or your decision featured in media?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.featuredInMedia, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),
        ]).filter((x): x is ISectionOptions["children"][0] => x !== null),
      },
    ],
  });

  return new Blob([await Packer.toBuffer(document)]);
};
